import { api } from './client';

export const outlineApi = {
  // ==========================================================================
  // STORY-LEVEL OUTLINE ENDPOINTS
  // ==========================================================================

  getOutline: (storyId) =>
    api.get(`/stories/${storyId}/outline`),

  createOutline: (storyId, templateKey = null) =>
    api.post(`/stories/${storyId}/outline`, { template_key: templateKey }),

  // ==========================================================================
  // PROJECT-LEVEL OUTLINE ENDPOINTS
  // ==========================================================================

  getProjectOutline: (projectId) =>
    api.get(`/projects/${projectId}/outline`),

  createProjectOutline: (projectId, medium, templateKey = null) =>
    api.post(`/projects/${projectId}/outline`, {
      medium,
      template_key: templateKey
    }),

  // ==========================================================================
  // TEMPLATES
  // ==========================================================================

  getTemplates: (medium = null, scope = null) => {
    const params = new URLSearchParams();
    if (medium) params.append('medium', medium);
    if (scope) params.append('scope', scope);
    const queryString = params.toString();
    return api.get(`/outline-templates${queryString ? `?${queryString}` : ''}`);
  },

  // Delete entire outline
  deleteOutline: (outlineId) =>
    api.delete(`/outlines/${outlineId}`),

  // Section CRUD
  createSection: (outlineId, data) =>
    api.post(`/outlines/${outlineId}/sections`, data),

  updateSection: (sectionId, data) =>
    api.patch(`/sections/${sectionId}`, data),

  // cascade=true must actually delete descendants: the backend reads
  // `mode` (strict|orphan|recursive) and silently ignored the old
  // `cascade` param, orphaning children on every delete (#179 finding).
  deleteSection: (sectionId, cascade = true) =>
    api.delete(`/sections/${sectionId}?mode=${cascade ? 'recursive' : 'orphan'}`),

  reorderSections: (outlineId, sectionIds, parentSectionId = null) =>
    api.patch(`/outlines/${outlineId}/sections/order`, {
      section_ids: sectionIds,
      parent_section_id: parentSectionId
    }),

  // Workshop sessions
  getWorkshops: (sectionId) =>
    api.get(`/sections/${sectionId}/workshops`),

  createWorkshop: (sectionId, name, description = null) =>
    api.post(`/sections/${sectionId}/workshops`, { name, description }),

  linkSession: (sessionId, sectionId) =>
    api.post(`/sessions/${sessionId}/link-section`, { section_id: sectionId }),

  unlinkSession: (sessionId) =>
    api.delete(`/sessions/${sessionId}/link-section`),

  // Focus/Scene session Connect action (plot-dossier-spec.md): "create a new
  // scene" branch — lands unplaced (parent_section_id=null). The "link to an
  // existing scene" branch reuses linkSession above unchanged.
  connectNewScene: (sessionId, displayName, description = null) =>
    api.post(`/sessions/${sessionId}/connect-new-scene`, {
      display_name: displayName,
      description,
    }),

  // Generation
  generateSection: (sectionId) =>
    api.post(`/sections/${sectionId}/generate`),

  generateOutline: (outlineId) =>
    api.post(`/outlines/${outlineId}/generate`),

  // Versions
  getVersions: (outlineId) =>
    api.get(`/outlines/${outlineId}/versions`),

  getVersion: (outlineId, versionNumber) =>
    api.get(`/outlines/${outlineId}/versions/${versionNumber}`),

  compareVersions: (outlineId, versionA, versionB) =>
    api.get(`/outlines/${outlineId}/versions/compare?version_a=${versionA}&version_b=${versionB}`),

  // Export
  getExportFormats: () =>
    api.get('/outline-exports/formats'),

  getExportUrl: (outlineId, format, version = null) => {
    const params = new URLSearchParams({ format });
    if (version) params.append('version', version.toString());
    const baseUrl = process.env.REACT_APP_API_URL || '/api';
    return `${baseUrl}/outlines/${outlineId}/export?${params}`;
  }
};
